import * as React from 'react';

// --- Tiptap UI ---
import type { UseTurnIntoDropdownConfig } from './use-turn-into-dropdown';
import { useTurnIntoDropdown } from './use-turn-into-dropdown';

// --- Hooks ---
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';
import { getAvailableTurnIntoConfigurations } from '../../../../app/rte/lib/turn-into-utils';

// --- Tiptap UI Components ---
// (Removed unused component imports)

// --- UI Components ---
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@cotera/client/v0/components/ui/popover';
import { Icon } from '@cotera/client/app/components';

export interface TurnIntoDropdownContentProps {
  blockTypes?: string[];
}

export const TurnIntoDropdownContent: React.FC<
  TurnIntoDropdownContentProps
> = ({ blockTypes: _blockTypes }) => {
  const { editor } = useTiptapEditor();
  const turnIntoConfigs = getAvailableTurnIntoConfigurations(editor);

  return (
    <div className="max-w-[200px] p-2">
      <div className="text-sm font-medium text-gray-700 mb-2">Turn into</div>
      <div className="space-y-1">
        {turnIntoConfigs.map((config, index) => (
          <TurnIntoButton
            key={`turn-into-${index}`}
            config={config}
            editor={editor}
          />
        ))}
      </div>
    </div>
  );
};

const TurnIntoButton: React.FC<{
  config: ReturnType<typeof getAvailableTurnIntoConfigurations>[0];
  editor: any;
}> = ({ config, editor }) => {
  const handleClick = () => {
    if (!editor) {
      return;
    }
    config.execute(editor);
  };

  return (
    <Button
      variant="ghost"
      className="w-full justify-start h-8 px-2 text-sm"
      onClick={handleClick}
    >
      <Icon icon={config.icon} className="w-4 h-4 mr-2 text-gray-500" />
      <span className="text-gray-900">{config.label}</span>
    </Button>
  );
};

export interface TurnIntoDropdownProps
  extends Omit<React.ComponentProps<typeof Button>, 'type'>,
    UseTurnIntoDropdownConfig {}

/**
 * Popover component for transforming block types in a Tiptap editor.
 * For custom popover implementations, use the `useTurnIntoDropdown` hook instead.
 */
export const TurnIntoDropdown = React.forwardRef<
  HTMLButtonElement,
  TurnIntoDropdownProps
>(
  (
    {
      editor: providedEditor,
      hideWhenUnavailable = false,
      blockTypes,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const [isOpen, setIsOpen] = React.useState(false);
    const { isVisible, canToggle, activeBlockType, label } =
      useTurnIntoDropdown({
        editor,
        hideWhenUnavailable,
        blockTypes,
      });

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        setIsOpen(!isOpen);
      },
      [onClick, isOpen]
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canToggle}
            data-disabled={!canToggle}
            role="button"
            tabIndex={-1}
            aria-label={label}
            onClick={handleClick}
            {...buttonProps}
            ref={ref}
          >
            {children ?? (
              <span className="tiptap-button-text">
                {activeBlockType?.label || 'Text'}
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          aria-label="Turn into options"
          side="bottom"
          align="start"
          sideOffset={0}
          className="!px-0"
        >
          <TurnIntoDropdownContent blockTypes={blockTypes} />
        </PopoverContent>
      </Popover>
    );
  }
);

TurnIntoDropdown.displayName = 'TurnIntoDropdown';
